// src/components/Document/SharePointFolderPicker.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Folder,
  FolderOpen,
  Search,
  ArrowBack,
  Check,
  CreateNewFolder,
  Close,
  ExpandMore,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { FileMetadata } from '../../types';

interface SharePointFolderPickerProps {
  projectId: number;
  selectedPath: string | null; // Project-relative path (e.g., '/Subfolder1/Subfolder2')
  onPathChange: (path: string) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  libraryName?: string | null; // Browse a specific destination; omit for system default
}

const SharePointFolderPicker = ({
  projectId,
  selectedPath,
  onPathChange,
  disabled = false,
  label = 'Target Folder',
  helperText = 'Click Browse to select or create a folder within the project',
  required = false,
  error = false,
  errorMessage,
  libraryName = null,
}: SharePointFolderPickerProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [items, setItems] = useState<FileMetadata[]>([]);
  // Current path is project-relative (e.g., ['Subfolder1', 'Subfolder2'])
  const [currentPath, setCurrentPath] = useState<string[]>(
    selectedPath ? selectedPath.split('/').filter(p => p) : []
  );
  
  // New folder creation state
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [createFolderError, setCreateFolderError] = useState<string | null>(null);

  // Load folders when dialog opens or path changes
  useEffect(() => {
    if (!open) return;

    const fetchFolders = async () => {
      setLoading(true);
      setFolderError(null);
      
      try {
        const pathString = currentPath.length > 0 ? currentPath.join('/') : undefined;
        const response = await apiService.listProjectSharePointFiles(
          projectId,
          pathString,
          libraryName || undefined
        );
        // Filter to only show folders
        const folders = response.items.filter(item => item.isFolder);
        setItems(folders);
      } catch (err) {
        let errorMessage = 'Failed to load folders';
        
        // Handle axios errors with response data
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        // Check for 403 Forbidden (out of scope)
        if (errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('outside project scope')) {
          // This is likely a backend validation bug - the folder exists but backend rejects listing its contents
          // For newly created folders, this is expected (they're empty anyway)
          // Silently show empty list - this is correct behavior for empty folders
          setItems([]);
          // Log for debugging but don't show error to user (empty folders are expected)
          console.warn('Backend validation issue: Cannot list folder contents, but folder exists. Showing empty list.');
        } else {
          setFolderError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [open, projectId, currentPath.join('/'), libraryName]);

  // Update current path when selectedPath prop changes
  useEffect(() => {
    if (selectedPath) {
      setCurrentPath(selectedPath.split('/').filter(p => p));
    } else {
      setCurrentPath([]);
    }
  }, [selectedPath]);

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFolderClick = (folder: FileMetadata) => {
    // Navigate into folder
    const newPath = [...currentPath, folder.name];
    setCurrentPath(newPath);
    setShowNewFolderInput(false);
    setNewFolderName('');
  };

  const handleBreadcrumbClick = (index: number) => {
    // Navigate to breadcrumb level (within project scope)
    const newPath = currentPath.slice(0, index);
    setCurrentPath(newPath);
    setShowNewFolderInput(false);
    setNewFolderName('');
  };

  const handleBackClick = () => {
    // Go back one level (within project scope)
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      setShowNewFolderInput(false);
      setNewFolderName('');
    }
  };

  const handleSelect = () => {
    // Select current path
    const pathString = currentPath.length > 0 ? '/' + currentPath.join('/') : '/';
    onPathChange(pathString);
    setOpen(false);
    setShowNewFolderInput(false);
    setNewFolderName('');
  };

  const handleCancel = () => {
    // Reset to selected path
    setCurrentPath(selectedPath ? selectedPath.split('/').filter(p => p) : []);
    setOpen(false);
    setShowNewFolderInput(false);
    setNewFolderName('');
    setCreateFolderError(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setCreateFolderError('Folder name is required');
      return;
    }

    // Validate folder name
    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(newFolderName)) {
      setCreateFolderError('Folder name cannot contain: \\ / : * ? " < > |');
      return;
    }

    setCreatingFolder(true);
    setCreateFolderError(null);

    try {
      // Build the path for the new folder relative to project root
      // The backend expects a relative path from the project root
      // If we're at project root (currentPath is empty), pass just the folder name
      // If we're in a subfolder, pass the relative path including the folder name
      const trimmedName = newFolderName.trim();
      let newFolderPath: string;
      
      if (currentPath.length === 0) {
        // At project root - pass just the folder name (backend should create at project root)
        newFolderPath = trimmedName;
      } else {
        // In a subfolder - pass the relative path from project root
        newFolderPath = `${currentPath.join('/')}/${trimmedName}`;
      }

      // Call the API to create the folder
      // Note: The backend should handle creating the folder at the project root
      // when folderPath is just a folder name (not a full path)
      await apiService.createProjectFolder(projectId, newFolderPath, libraryName || undefined);

      // Reset the new folder input
      setShowNewFolderInput(false);
      setNewFolderName('');
      
      // Refresh the current folder's list to show the newly created folder
      // Don't navigate into it automatically - let the user click to navigate
      // This avoids the backend validation issue when listing contents of newly created folders
      const pathString = currentPath.length > 0 ? currentPath.join('/') : undefined;
      const response = await apiService.listProjectSharePointFiles(projectId, pathString, libraryName || undefined);
      const folders = response.items.filter(item => item.isFolder);
      setItems(folders);
      
      // The newly created folder will now appear in the list above
    } catch (err: unknown) {
      let errorMessage = 'Failed to create folder';
      
      // Handle axios errors with response data
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      // Check for specific backend validation errors
      if (errorMessage.includes('outside project scope')) {
        // Backend validation error - provide helpful guidance
        // The backend may be incorrectly validating relative paths at project root
        errorMessage = `Unable to create folder at project root: ${errorMessage}. ` +
          `Please try creating the folder in a subdirectory first, or contact the backend team if this persists.`;
      } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        errorMessage = 'Permission denied: You do not have permission to create folders in this location.';
      } else if (errorMessage.includes('409') || errorMessage.includes('Conflict') || errorMessage.includes('already exists')) {
        errorMessage = 'A folder with this name already exists in this location.';
      } else if (errorMessage.includes('must start with /')) {
        // Backend expects paths to start with / - but this conflicts with relative path support
        // This might be a backend validation issue
        errorMessage = `Invalid path format: ${errorMessage}. Please contact support.`;
      }
      
      setCreateFolderError(errorMessage);
    } finally {
      setCreatingFolder(false);
    }
  };

  // Get display path
  const getDisplayPath = () => {
    if (currentPath.length === 0) {
      return 'Project Root';
    }
    return '/' + currentPath.join('/');
  };

  // Get selected path display for the text field
  const getSelectedPathDisplay = () => {
    if (!selectedPath) {
      return 'Not selected';
    }
    if (selectedPath === '/') {
      return 'Project Root';
    }
    return selectedPath;
  };

  return (
    <>
      <TextField
        label={label}
        value={getSelectedPathDisplay()}
        onClick={() => !disabled && setOpen(true)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Folder color={selectedPath ? 'primary' : 'disabled'} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) setOpen(true);
                }}
                disabled={disabled}
              >
                Browse
              </Button>
            </InputAdornment>
          ),
        }}
        fullWidth
        disabled={disabled}
        required={required}
        error={error}
        helperText={errorMessage || helperText}
        sx={{
          '& .MuiInputBase-input': {
            cursor: disabled ? 'not-allowed' : 'pointer',
          },
        }}
      />

      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Select Target Folder</Typography>
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{ ml: 2 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Breadcrumbs */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2, 
              p: 1, 
              bgcolor: 'grey.100', 
              borderRadius: 1 
            }}>
              <FolderOpen sx={{ mr: 1, color: 'primary.main' }} />
              <Breadcrumbs>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setCurrentPath([])}
                  sx={{ 
                    cursor: 'pointer', 
                    fontWeight: currentPath.length === 0 ? 'bold' : 'normal',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Project Root
                </Link>
                {currentPath.map((segment, index) => (
                  <Link
                    key={index}
                    component="button"
                    variant="body2"
                    onClick={() => handleBreadcrumbClick(index + 1)}
                    sx={{ 
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {segment}
                  </Link>
                ))}
              </Breadcrumbs>
            </Box>

            {/* Back button and New Folder button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                {currentPath.length > 0 && (
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackClick}
                    size="small"
                  >
                    Back
                  </Button>
                )}
              </Box>
              <Button
                startIcon={<CreateNewFolder />}
                onClick={() => setShowNewFolderInput(!showNewFolderInput)}
                size="small"
                color="primary"
                variant={showNewFolderInput ? 'contained' : 'outlined'}
              >
                New Folder
              </Button>
            </Box>

            {/* New Folder Input */}
            <Collapse in={showNewFolderInput}>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Create New Folder in: {getDisplayPath()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter folder name..."
                    value={newFolderName}
                    onChange={(e) => {
                      setNewFolderName(e.target.value);
                      setCreateFolderError(null);
                    }}
                    error={!!createFolderError}
                    helperText={createFolderError}
                    disabled={creatingFolder}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFolderName.trim()) {
                        handleCreateFolder();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Folder />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreateFolder}
                    disabled={creatingFolder || !newFolderName.trim()}
                    sx={{ minWidth: 100, height: 40 }}
                  >
                    {creatingFolder ? <CircularProgress size={20} /> : 'Create'}
                  </Button>
                </Box>
              </Box>
            </Collapse>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            {/* Loading */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Error */}
            {folderError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {folderError}
              </Alert>
            )}

            {/* Folder List */}
            {!loading && !folderError && (
              <Box sx={{ 
                maxHeight: 300, 
                overflow: 'auto', 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}>
                <List disablePadding>
                  {filteredItems.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {searchTerm 
                              ? 'No folders found matching your search' 
                              : 'No subfolders in this directory'
                            }
                          </Typography>
                        }
                      />
                    </ListItem>
                  ) : (
                    filteredItems.map((folder) => (
                      <ListItem key={folder.itemId} disablePadding divider>
                        <ListItemButton onClick={() => handleFolderClick(folder)}>
                          <ListItemIcon>
                            <Folder color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={folder.name}
                            primaryTypographyProps={{ noWrap: true }}
                          />
                          <ExpandMore color="action" />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            )}

            {/* Selected Path Display */}
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'primary.light', 
              borderRadius: 1,
              border: '2px solid',
              borderColor: 'primary.main',
            }}>
              <Typography variant="body2" color="primary.contrastText" sx={{ fontWeight: 'medium' }}>
                Selected Folder:
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
                {getDisplayPath()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleSelect}
            variant="contained"
            startIcon={<Check />}
            color="primary"
          >
            Select This Folder
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SharePointFolderPicker;

